import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Heart, ArrowLeft, Save, Users, MapPin, Camera, Music, 
  Utensils, Clock, Sparkles, ChevronDown, ChevronUp, 
  Check, AlertCircle, MessageCircle, User, Phone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import VALAssistant from '../components/VALAssistant';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

// Section icons mapping
const sectionIcons = {
  couple_basics: Users,
  vendor_team: Phone,
  getting_ready: User,
  ceremony_details: Heart,
  cocktail_hour: Music,
  reception_flow: Sparkles,
  dinner_details: Utensils,
  special_moments: Camera,
  open_dance: Music,
  overall_vision: Heart
};

const Details = ({ user }) => {
  const navigate = useNavigate();
  const [details, setDetails] = useState({});
  const [questions, setQuestions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [showVAL, setShowVAL] = useState(false);

  useEffect(() => {
    loadData();
  }, [user.id]);

  const loadData = async () => {
    try {
      const [detailsRes, questionsRes] = await Promise.all([
        axios.get(`${API}/wedding-details/${user.id}`),
        axios.get(`${API}/wedding-details/${user.id}/questions`)
      ]);
      setDetails(detailsRes.data);
      setQuestions(questionsRes.data);
      
      // Expand first section by default
      const firstSection = Object.keys(questionsRes.data)[0];
      if (firstSection) {
        setExpandedSections({ [firstSection]: true });
      }
    } catch (error) {
      console.error('Error loading details:', error);
      toast.error('Failed to load wedding details');
    } finally {
      setLoading(false);
    }
  };

  // Get unanswered questions for Grace to ask
  const getUnansweredQuestions = () => {
    const unanswered = [];
    Object.entries(questions).forEach(([sectionKey, section]) => {
      section.questions.forEach(q => {
        const value = details[q.id];
        if (value === null || value === undefined || value === '') {
          unanswered.push({
            ...q,
            section: section.title
          });
        }
      });
    });
    return unanswered;
  };

  const handleChange = (fieldId, value) => {
    setDetails(prev => ({ ...prev, [fieldId]: value }));
    setUnsavedChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/wedding-details/${user.id}`, details);
      toast.success('Wedding details saved!');
      setUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving details:', error);
      toast.error('Failed to save details');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const getCompletionPercentage = (sectionKey) => {
    const section = questions[sectionKey];
    if (!section) return 0;
    
    const answered = section.questions.filter(q => {
      const value = details[q.id];
      return value !== null && value !== undefined && value !== '';
    }).length;
    
    return Math.round((answered / section.questions.length) * 100);
  };

  const getTotalCompletion = () => {
    const allQuestions = Object.values(questions).flatMap(s => s.questions);
    if (allQuestions.length === 0) return 0;
    
    const answered = allQuestions.filter(q => {
      const value = details[q.id];
      return value !== null && value !== undefined && value !== '';
    }).length;
    
    return Math.round((answered / allQuestions.length) * 100);
  };

  const renderField = (question) => {
    const value = details[question.id] || '';
    
    switch (question.type) {
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleChange(question.id, e.target.value)}
            className="w-full px-4 py-2.5 rounded-lg border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#881337]/20 focus:border-[#881337] transition-all text-[#1C1917]"
          >
            <option value="">Select...</option>
            {question.options?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      
      case 'textarea':
        return (
          <Textarea
            value={value}
            onChange={(e) => handleChange(question.id, e.target.value)}
            placeholder={question.helper || ''}
            className="w-full min-h-[80px] rounded-lg border-stone-200 focus:ring-[#881337]/20 focus:border-[#881337]"
          />
        );
      
      case 'number':
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) => handleChange(question.id, parseInt(e.target.value) || '')}
            placeholder={question.helper || ''}
            className="w-full rounded-lg border-stone-200 focus:ring-[#881337]/20 focus:border-[#881337]"
          />
        );
      
      case 'date':
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => handleChange(question.id, e.target.value)}
            className="w-full rounded-lg border-stone-200 focus:ring-[#881337]/20 focus:border-[#881337]"
          />
        );
      
      case 'time':
        return (
          <Input
            type="time"
            value={value}
            onChange={(e) => handleChange(question.id, e.target.value)}
            className="w-full rounded-lg border-stone-200 focus:ring-[#881337]/20 focus:border-[#881337]"
          />
        );
      
      default:
        return (
          <Input
            type="text"
            value={value}
            onChange={(e) => handleChange(question.id, e.target.value)}
            placeholder={question.helper || ''}
            className="w-full rounded-lg border-stone-200 focus:ring-[#881337]/20 focus:border-[#881337]"
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-rose-50/30 to-stone-100 flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <Heart className="w-12 h-12 text-[#881337] animate-pulse" />
          <p className="text-[#57534E]">Loading your details...</p>
        </div>
      </div>
    );
  }

  const totalCompletion = getTotalCompletion();

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-rose-50/30 to-stone-100">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-[#57534E] hover:text-[#881337]"
          >
            <ArrowLeft className="mr-2" size={18} />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Heart className="text-[#881337]" size={24} fill="#881337" />
            <span className="text-xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="font-serif text-2xl sm:text-3xl text-[#1C1917]">Wedding Details</h1>
          <p className="text-[#57534E] mt-1">All your wedding information in one place</p>
        </div>

        {/* Overall Progress */}
        <div className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/50 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-[#881337] to-[#9F1239] rounded-xl flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-serif text-lg text-[#1C1917]">Overall Completion</h2>
                <p className="text-sm text-[#57534E]">{totalCompletion}% of details filled</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowVAL(true)}
                className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-full hover:bg-amber-100 transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Ask Grace ({getUnansweredQuestions().length} remaining)</span>
              </button>
              
              <Button
                onClick={handleSave}
                disabled={saving || !unsavedChanges}
                className="rounded-full bg-[#881337] hover:bg-[#9F1239] text-white disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
          
          <div className="w-full bg-stone-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-[#881337] to-[#9F1239] h-3 rounded-full transition-all duration-500"
              style={{ width: `${totalCompletion}%` }}
            />
          </div>
          
          {unsavedChanges && (
            <div className="mt-4 flex items-center gap-2 text-amber-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>You have unsaved changes</span>
            </div>
          )}
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {Object.entries(questions).map(([sectionKey, section]) => {
            const Icon = sectionIcons[sectionKey] || Heart;
            const isExpanded = expandedSections[sectionKey];
            const completion = getCompletionPercentage(sectionKey);
            
            return (
              <div 
                key={sectionKey}
                className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/50 overflow-hidden"
              >
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(sectionKey)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-stone-50/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      completion === 100 
                        ? 'bg-green-100 text-green-600' 
                        : 'bg-stone-100 text-[#57534E]'
                    }`}>
                      {completion === 100 ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <div className="text-left">
                      <h3 className="font-serif text-lg text-[#1C1917]">{section.title}</h3>
                      <p className="text-sm text-[#57534E]">{completion}% complete • {section.questions.length} questions</p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-[#57534E]" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#57534E]" />
                  )}
                </button>
                
                {/* Section Content */}
                {isExpanded && (
                  <div className="px-6 pb-6 border-t border-stone-100">
                    <div className="grid gap-6 pt-6">
                      {section.questions.map(question => (
                        <div key={question.id}>
                          <label className="block text-sm font-medium text-[#1C1917] mb-2">
                            {question.label}
                            {details[question.id] && (
                              <Check className="w-4 h-4 text-green-500 inline ml-2" />
                            )}
                          </label>
                          {question.helper && (
                            <p className="text-xs text-[#57534E] mb-2">{question.helper}</p>
                          )}
                          {renderField(question)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom Save Button */}
        {unsavedChanges && (
          <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-40">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="rounded-full bg-[#881337] hover:bg-[#9F1239] text-white shadow-lg px-8 py-3"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save All Changes
                </>
              )}
            </Button>
          </div>
        )}
      </main>

      {/* Grace Assistant Modal */}
      {showVAL && (
        <VALAssistant 
          user={user} 
          onClose={() => {
            setShowVAL(false);
            // Reload details after Grace session to get any updates
            loadData();
          }}
          mode="details"
          unansweredQuestions={getUnansweredQuestions()}
        />
      )}
    </div>
  );
};

export default Details;
